import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  InputNumber,
  InputNumberProps,
  message,
  Radio,
  Segmented,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import DrawerCustom from 'lib.components/src/components/DrawerCustom';
import {
  BankOutlined,
  CloseOutlined,
  CopyOutlined,
  CreditCardOutlined,
  DownloadOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import './styles.scss';
import { useBreakpoints } from 'lib.hooks/src';
import { useAuth } from 'lib.context/src/AuthContext';
import {
  useGetBillingInfoQuery,
  usePostBillingTopUpMutation,
  usePostWireInvoiceMutation,
  usePostWireInvoicePreviewMutation,
} from 'lib.store/src/main/billing/billing.api';
import { usePrecognitionForm } from 'lib.hooks/src';
import { BILLING_ENDPOINTS } from 'lib.store/src/main/billing/endpoints';
import { SubscriptionName } from 'lib.store/src/main/subscription/models';
import { usePostSubscriptionMutation } from 'lib.store/src/main/subscription/subscription.api';
import {
  BillingInfoSubscription,
  WireInvoiceItem,
  WireInvoicePreviewResponse,
  WireInvoiceResource,
} from 'lib.store/src/main/billing/models';
import { TextBodyRegular, TextBodySemiBold, TextH5Bold } from 'lib.typography';
import {
  formatCurrency,
  formatCurrencyAmount,
  formatMetricNumber,
  getCurrencySymbol,
} from 'lib.shared/src/numberFormat';
import { useAppSelector } from 'lib.hooks/src/redux';
import { RootState } from 'lib.store/src';
import { UserBillingPageContext } from 'lib.context/src/UserBillingPageContext';

type PaymentMethodOption = 'card' | 'wire_transfer';

interface TopUpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  initialAmount?: number;
  subscriptionId?: number;
}

// Debounce delay for preview calls (ms)
const PREVIEW_DEBOUNCE_MS = 600;

/** the bonus gift, on currentColor (the gradient belongs to PRO's own marks only) */
const GiftIcon: React.FC = () => <GiftOutlined aria-hidden />;

// ── Shared sub-components ────────────────────────────────────────────

interface PaymentMethodSelectorProps {
  value: PaymentMethodOption;
  onChange: (value: PaymentMethodOption) => void;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  value,
  onChange,
}) => (
  <div className="payment-method-selector">
    <span className="topup-label">Payment method</span>
    <Segmented<PaymentMethodOption>
      block
      value={value}
      onChange={(next) => onChange(next)}
      className="payment-method-selector__options"
      options={[
        {
          value: 'card',
          label: (
            <span className="payment-method-option__label">
              <CreditCardOutlined /> Card
            </span>
          ),
        },
        {
          value: 'wire_transfer',
          label: (
            <span className="payment-method-option__label">
              <BankOutlined /> Wire transfer
            </span>
          ),
        },
      ]}
    />
  </div>
);

interface SubscriptionMonthsSelectorProps {
  months: number;
  onChange: (months: number) => void;
}

export const SubscriptionMonthsSelector: React.FC<SubscriptionMonthsSelectorProps> = ({
  months,
  onChange,
}) => (
  <div className="subscription-months-selector">
    <span>Number of months</span>
    <InputNumber min={1} max={36} value={months} onChange={(v) => v && onChange(v)} />
  </div>
);

// ── Helper: map preview items to display rows ────────────────────────

/** Build a lookup map from billing info subscriptions: plan.name → plan.translate */
export const buildSubscriptionTranslateMap = (
  subscriptions: BillingInfoSubscription[] | undefined,
): Record<string, string> => {
  const map: Record<string, string> = {};
  subscriptions?.forEach((s) => {
    if (s.plan?.name && s.plan?.translate) {
      map[s.plan.name] = s.plan.translate;
    }
  });
  return map;
};

const resolveSubscriptionName = (
  name: string | undefined | null,
  translateMap: Record<string, string>,
): string => {
  if (!name) return 'Subscription';
  return translateMap[name] ?? name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

interface OrderSummaryRow {
  label: string;
  detail?: string;
  amount: number;
  isFree?: boolean;
}

export const mapPreviewItemsToRows = (
  items: WireInvoiceItem[],
  translateMap: Record<string, string> = {},
  currencyCode = 'USD',
): OrderSummaryRow[] =>
  items.map((item) => {
    if (item.type === 'subscription') {
      const formattedPrice = formatCurrencyAmount(item.price_per_month, currencyCode, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return {
        label: resolveSubscriptionName(item.subscription_type_name, translateMap),
        detail: `${item.months} ${item.months === 1 ? 'month' : 'months'} × ${formattedPrice}/mo`,
        amount: item.amount,
      };
    }
    if (item.type === 'bonus') {
      return {
        label: `${resolveSubscriptionName(item.subscription_type_name, translateMap)} — ${item.months} ${item.months === 1 ? 'month' : 'months'}`,
        isFree: true,
        amount: 0,
      };
    }
    // balance
    return {
      label: 'Balance top-up',
      amount: item.amount,
    };
  });

// ── OrderSummary (supports isFree flag) ──────────────────────────────

interface OrderSummaryProps {
  items: OrderSummaryRow[];
  total: number;
  currencyCode?: string;
  isLoading?: boolean;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  total,
  currencyCode = 'USD',
  isLoading,
}) => (
  <div className="order-summary">
    <span className="order-summary__title">Order summary</span>
    {isLoading ? (
      <div className="order-summary__loading">
        <Spin size="small" />
      </div>
    ) : (
      <>
        <div className="order-summary__items">
          {items.map((item, idx) => (
            <div key={idx} className="order-summary__item">
              <div className="order-summary__item-label">
                <span>
                  {item.isFree && <GiftOutlined className="order-summary__gift-icon" />}
                  {item.label}
                </span>
                {item.detail && <span className="order-summary__item-detail">{item.detail}</span>}
              </div>
              {item.isFree ? (
                <span className="order-summary__free-badge">Free</span>
              ) : (
                <TextBodySemiBold className="text-neutral-10">
                  {formatCurrencyAmount(item.amount, currencyCode, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </TextBodySemiBold>
              )}
            </div>
          ))}
        </div>
        <div className="order-summary__total">
          <TextBodySemiBold className="text-neutral-10">Total</TextBodySemiBold>
          <span className="order-summary__total-value">
            {formatCurrencyAmount(total, currencyCode, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </span>
        </div>
      </>
    )}
  </div>
);

// ── WireInvoiceInstructions (with bonus support) ─────────────────────

interface WireInvoiceInstructionsProps {
  wireInvoice: WireInvoiceResource;
  onCopyToClipboard: (text: string, label: string) => Promise<void>;
  translateMap?: Record<string, string>;
  currencyCode?: string;
}

export const WireInvoiceInstructions: React.FC<WireInvoiceInstructionsProps> = ({
  wireInvoice,
  onCopyToClipboard,
  translateMap = {},
  currencyCode = 'USD',
}) => {
  const {
    amount: invoiceAmount,
    items,
    bank_details,
    reference,
    number: invoiceNumber,
  } = wireInvoice;

  return (
    <div className="wire-invoice-instructions">
      <div className="wire-invoice-instructions__header">
        <TextH5Bold className="text-neutral-10">Wire transfer invoice created</TextH5Bold>
        <TextBodyRegular className="text-neutral-7 mt-4">
          Use the buttons below to copy bank details or download the invoice
        </TextBodyRegular>
      </div>

      {/* Invoice Info */}
      <div className="wire-invoice-instructions__section">
        <div className="flex justify-between items-center">
          <div>
            <span className="wire-invoice-instructions__label">Invoice</span>
            <TextBodySemiBold className="text-neutral-10 wire-invoice-instructions__value">
              #{invoiceNumber}
            </TextBodySemiBold>
          </div>
          <div>
            <span className="wire-invoice-instructions__label">Amount</span>
            <TextBodySemiBold className="text-neutral-10 wire-invoice-instructions__value">
              {formatCurrency(invoiceAmount.sum.toString())}
            </TextBodySemiBold>
          </div>
        </div>
      </div>

      {/* Line items breakdown */}
      {items && items.length > 0 && (
        <div className="wire-invoice-instructions__section wire-invoice-instructions__items">
          <span className="wire-invoice-instructions__label mb-8">Invoice items</span>
          {items.map((item: WireInvoiceItem, idx: number) => (
            <div key={idx} className="wire-invoice-instructions__line-item">
              {item.type === 'subscription' && (
                <>
                  <span>
                    {resolveSubscriptionName(item.subscription_type_name, translateMap)} —{' '}
                    {item.months} {item.months === 1 ? 'month' : 'months'} ×{' '}
                    {formatCurrencyAmount(item.price_per_month, currencyCode, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    /mo
                  </span>
                  <TextBodySemiBold className="text-neutral-10">
                    {formatCurrency(item.amount.toString())}
                  </TextBodySemiBold>
                </>
              )}
              {item.type === 'balance' && (
                <>
                  <span>Balance top-up</span>
                  <TextBodySemiBold className="text-neutral-10">
                    {formatCurrency(item.amount.toString())}
                  </TextBodySemiBold>
                </>
              )}
              {item.type === 'bonus' && (
                <>
                  <span>
                    <GiftOutlined className="order-summary__gift-icon" />
                    {resolveSubscriptionName(item.subscription_type_name, translateMap)} —{' '}
                    {item.months} {item.months === 1 ? 'month' : 'months'}
                  </span>
                  <span className="order-summary__free-badge">Free</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Payment Reference - Critical */}
      <div className="wire-invoice-instructions__section wire-invoice-instructions__reference">
        <div className="flex justify-between items-center">
          <div>
            <span className="wire-invoice-instructions__label">Payment reference (required)</span>
            <TextBodySemiBold className="text-neutral-10 wire-invoice-instructions__value">
              {reference}
            </TextBodySemiBold>
          </div>
          <Button
            type="text"
            icon={<CopyOutlined />}
            aria-label="Copy payment reference"
            onClick={() => void onCopyToClipboard(reference, 'Payment reference')}
          />
        </div>
        <span className="wire-invoice-instructions__reference-note">
          Include this reference in your payment to ensure proper allocation
        </span>
      </div>

      {/* Processing Timeline */}
      <div className="wire-invoice-instructions__section wire-invoice-instructions__timeline">
        <span className="wire-invoice-instructions__label">Processing timeline</span>
        <span>{bank_details.processing_timeline}</span>
      </div>

      {/* Email Note */}
      <div className="wire-invoice-instructions__note">
        <span>A copy of this invoice has been sent to your billing email address</span>
      </div>
    </div>
  );
};

// ── Custom hook: debounced wire invoice preview ──────────────────────

export const useWireInvoicePreview = () => {
  const [postPreview] = usePostWireInvoicePreviewMutation();
  const [preview, setPreview] = useState<WireInvoicePreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // only the newest request may land — an older amount's answer arriving late
  // would otherwise replace the preview of the amount on screen
  const requestRef = useRef(0);

  const fetchPreview = useCallback(
    (body: Parameters<typeof postPreview>[0]) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const request = ++requestRef.current;

      setPreviewLoading(true);
      setPreviewFailed(false);
      debounceRef.current = setTimeout(() => {
        postPreview(body)
          .then((res) => {
            if (request !== requestRef.current) return;
            if ('data' in res) {
              setPreview(res.data ?? null);
            } else {
              setPreviewFailed(true);
            }
          })
          .catch(() => {
            // the wire invoice can still be created without it; a card order
            // that waits on it offers a retry instead
            if (request === requestRef.current) setPreviewFailed(true);
          })
          .finally(() => {
            if (request === requestRef.current) setPreviewLoading(false);
          });
      }, PREVIEW_DEBOUNCE_MS);
    },
    [postPreview],
  );

  const clearPreview = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestRef.current += 1;
    setPreview(null);
    setPreviewLoading(false);
    setPreviewFailed(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { preview, previewLoading, previewFailed, fetchPreview, clearPreview };
};

// ── Main TopUpDrawer Component ───────────────────────────────────────

const TopUpDrawer: React.FC<TopUpDrawerProps> = ({ isOpen, onClose, initialAmount }) => {
  const { md } = useBreakpoints();
  const { reFetchUser, user: fetchedUser } = useAuth();
  const userState = useAppSelector((state: RootState) => state.userAuth.user);
  const user = userState || fetchedUser;
  const { data: billingInfo } = useGetBillingInfoQuery();
  const subscriptionTranslateMap = useMemo(
    () => buildSubscriptionTranslateMap(billingInfo?.data?.subscriptions),
    [billingInfo?.data?.subscriptions],
  );
  const boostProSubscription = billingInfo?.data?.subscriptions?.find(
    (s: BillingInfoSubscription) => s.plan?.name === SubscriptionName.boost_pro,
  );
  const topUpBonusRaw = boostProSubscription?.money?.top_up_bonus;
  const topUpStatusName = user?.organization?.billing?.top_up_status?.name as string | undefined;
  const canShowTopUpBonus = topUpStatusName === 'none' || !topUpStatusName;
  const topUpBonus = canShowTopUpBonus ? topUpBonusRaw : undefined;
  const topUpBonusAmounts = topUpBonusRaw;
  const { topUpAmount, setTopUpAmount } = useContext(UserBillingPageContext);

  const subscriptionPricePerMonth = boostProSubscription?.money?.price || 0;

  const getAmountOptions = (): number[] => {
    // the quick amounts and the bonus tiers both come from the backend config
    const baseAmounts = boostProSubscription?.money?.top_up_amounts ?? [];
    const bonusAmounts = topUpBonusAmounts?.map((tier) => tier.amount) ?? [];
    const allAmounts = [...baseAmounts, ...bonusAmounts];
    const uniqueAmounts = [...new Set(allAmounts)];
    return uniqueAmounts.sort((a, b) => b - a);
  };

  const getTierByAmount = (amount: number) => topUpBonus?.find((tier) => tier.amount === amount);

  const amountOptions = getAmountOptions();

  const [postTopUp] = usePostBillingTopUpMutation();
  const [postSubscription] = usePostSubscriptionMutation();
  const [postWireInvoice] = usePostWireInvoiceMutation();
  const { preview, previewLoading, previewFailed, fetchPreview, clearPreview } =
    useWireInvoicePreview();

  const [topUpLoading, setTopUpLoading] = useState(false);
  const [amount, setAmount] = useState<number | null>(initialAmount || null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption>('wire_transfer');
  const [wireInvoice, setWireInvoice] = useState<WireInvoiceResource | null>(null);

  // Subscription add-on state (only for wire transfer)
  const [includeSubscription, setIncludeSubscription] = useState(false);
  const [subscriptionMonths, setSubscriptionMonths] = useState<number>(3);

  const topUpForm = usePrecognitionForm('post', BILLING_ENDPOINTS.topUp, {
    amount: initialAmount || null,
  });

  const currencyCode = user?.organization?.billing?.currency?.toUpperCase();
  const currencySymbol = useMemo(() => getCurrencySymbol(currencyCode), [currencyCode]);

  const formatAmount = (value: number): string =>
    formatCurrencyAmount(value, currencyCode ?? 'USD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  // Build the wire invoice request body (used for both preview and create)
  const buildWireBody = useCallback(() => {
    if (includeSubscription) {
      return {
        subscription_type: boostProSubscription?.plan?.id || 1,
        // a card subscription renews monthly — the card order prices its first month
        subscription_months: paymentMethod === 'card' ? 1 : subscriptionMonths,
        ...(amount && amount > 0 ? { balance_amount: amount } : {}),
      };
    }
    if (amount && amount > 0) {
      return { amount };
    }
    return null;
  }, [
    includeSubscription,
    subscriptionMonths,
    amount,
    boostProSubscription?.plan?.id,
    paymentMethod,
  ]);

  // Fetch the order preview: always for a wire invoice; for a card when it
  // carries Boost PRO — the summary then shows both charges the click makes
  useEffect(() => {
    if (paymentMethod !== 'wire_transfer' && !includeSubscription) {
      clearPreview();
      return;
    }
    const body = buildWireBody();
    if (body) {
      fetchPreview(body);
    } else {
      clearPreview();
    }
  }, [paymentMethod, includeSubscription, buildWireBody, fetchPreview, clearPreview]);

  // Map preview to order summary rows
  const previewRows = useMemo(
    () =>
      preview?.data?.items
        ? mapPreviewItemsToRows(preview.data.items, subscriptionTranslateMap, currencyCode ?? 'USD')
        : [],
    [preview, subscriptionTranslateMap, currencyCode],
  );

  const showOrderSummary =
    (paymentMethod === 'wire_transfer' || includeSubscription) &&
    (previewRows.length > 0 || previewLoading);

  // the PRO the card order actually charges: a paid month on the preview — a
  // top-up big enough for the bonus brings it free, so nothing is charged
  const cardProCharge =
    preview?.data?.items?.find((item) => item.type === 'subscription' && item.amount > 0) ?? null;
  // the preview the click acts on must be the one for the amount on screen —
  // a stale one could charge PRO on a top-up whose bonus already brings it
  const isPreviewCurrent =
    preview?.data?.items?.some((item) => item.type === 'balance' && item.amount === amount) ??
    false;
  // what the click charges to the card: the top-up, plus PRO's paid month when the
  // order carries it (the preview's total) — the button names the whole sum
  const cardPayTotal =
    includeSubscription && cardProCharge ? (preview?.data?.total ?? amount ?? 0) : (amount ?? 0);
  const isCardOrderPending =
    paymentMethod === 'card' &&
    includeSubscription &&
    !!amount &&
    !previewFailed &&
    (previewLoading || !isPreviewCurrent);

  const onChange: InputNumberProps['onChange'] = (value) => {
    const numericValue = value !== null ? +value : null;
    setAmount(numericValue);
    setTopUpAmount(numericValue);
    topUpForm.setField('amount', numericValue ?? 0);
  };

  useEffect(() => {
    if (topUpAmount) {
      setAmount(Number(topUpAmount));
    } else setAmount(null);
  }, [topUpAmount]);

  useEffect(() => {
    if (initialAmount) {
      setAmount(initialAmount);
    }
  }, [initialAmount]);

  // nothing launches without Boost PRO: an organization that has none gets it
  // on the order — on a wire invoice switched on (the backend counts a top-up
  // bonus month into it as free), on a card locked on and charged right after
  // the top-up, as its own payment
  const needsPro = user?.organization?.has_pro_access === false;
  useEffect(() => {
    if (!isOpen) return;
    setIncludeSubscription(needsPro);
  }, [paymentMethod, isOpen, needsPro]);

  const onSubmit = async () => {
    if (topUpLoading) return;

    if (paymentMethod === 'card') {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await topUpForm.validateField('amount');
      if (topUpForm.errors.amount || !amount) {
        void message.open({ type: 'error', content: topUpForm.errors.amount });
        return;
      }

      if (isCardOrderPending) return;

      // the order could not be priced: ask again rather than guess the PRO charge
      if (includeSubscription && previewFailed) {
        const body = buildWireBody();
        if (body) fetchPreview(body);
        void message.open({ type: 'warning', content: 'Pricing the order again — try once more' });
        return;
      }

      setTopUpLoading(true);
      try {
        const response = await postTopUp({ amount });
        if ('data' in response) {
          // the second payment of the order: Boost PRO on the same card
          if (includeSubscription && cardProCharge) {
            const subscription = await postSubscription();
            if ('error' in subscription) {
              const errorMessage =
                (subscription.error as { data?: { message?: string } })?.data?.message ||
                'Boost PRO payment failed';
              setTopUpLoading(false);
              setTopUpAmount(null);
              void reFetchUser({ silent: true });
              onClose();
              return message.open({
                type: 'error',
                content: `Balance has been topped up, but Boost PRO was not: ${errorMessage}`,
                duration: 8,
              });
            }
          }
          setTopUpLoading(false);
          setTopUpAmount(null);
          void reFetchUser({ silent: true });
          onClose();
          return message.open({
            type: 'success',
            content:
              includeSubscription && cardProCharge
                ? 'Balance topped up and Boost PRO is on'
                : 'Balance has been topped up',
          });
        }
        if ('error' in response) {
          const errorMessage =
            (response.error as { data?: { message?: string } })?.data?.message ||
            'Failed to top up balance';
          void message.open({ type: 'error', content: errorMessage });
        }
      } catch (error) {
        console.error('rejected', error);
        void message.open({ type: 'error', content: 'Failed to top up balance' });
      } finally {
        setTopUpLoading(false);
      }
    } else {
      // Wire transfer flow
      const body = buildWireBody();
      if (!body) {
        void message.open({
          type: 'error',
          content: 'Please enter a top-up amount or add a subscription',
        });
        return;
      }

      setTopUpLoading(true);
      try {
        const response = await postWireInvoice(body);
        if ('data' in response && response.data) {
          setTopUpLoading(false);
          setWireInvoice(response.data.data);
          return message.open({
            type: 'success',
            content: 'Wire transfer invoice created',
          });
        }
        if ('error' in response) {
          const errorMessage =
            (response.error as { data?: { message?: string } })?.data?.message ||
            'Failed to create wire transfer invoice';
          void message.open({ type: 'error', content: errorMessage });
        }
      } catch (error) {
        console.error('rejected', error);
        void message.open({ type: 'error', content: 'Failed to create wire transfer invoice' });
      } finally {
        setTopUpLoading(false);
      }
    }
  };

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      void message.success(`${label} copied to clipboard`);
    } catch {
      void message.error('Failed to copy to clipboard');
    }
  };

  const handleCopyBankDetails = () => {
    if (!wireInvoice?.bank_details) return;
    const { beneficiary, bank } = wireInvoice.bank_details;
    const details = [
      `Beneficiary: ${beneficiary.company_name}`,
      `Address: ${beneficiary.address}`,
      `Account Number: ${bank.account_number}`,
      bank.routing_number ? `Routing Number: ${bank.routing_number}` : null,
      `Bank: ${bank.name}`,
      `SWIFT/BIC: ${bank.swift_bic}`,
      bank.iban ? `IBAN: ${bank.iban}` : null,
      bank.intermediary_bank ? `Intermediary Bank: ${bank.intermediary_bank}` : null,
      bank.intermediary_swift_bic ? `Intermediary SWIFT: ${bank.intermediary_swift_bic}` : null,
      `Reference: ${wireInvoice.reference}`,
    ]
      .filter(Boolean)
      .join('\n');
    void handleCopyToClipboard(details, 'Bank details');
  };

  const handleDownloadInvoice = () => {
    if (!wireInvoice?.link) return;
    window.open(wireInvoice.link, '_blank');
  };

  const handleCloseDrawer = () => {
    setWireInvoice(null);
    setPaymentMethod('wire_transfer');
    setIncludeSubscription(false);
    setSubscriptionMonths(3);
    clearPreview();
    onClose();
  };

  const getTooltipTitle = (tier: { amount: number; months: number }) => (
    <div className="topup-tip">
      <span className="topup-tip__title">
        Top up {currencySymbol}
        {formatMetricNumber(tier.amount)} = {tier.months} {tier.months === 1 ? 'month' : 'months'}{' '}
        Boost PRO for free
      </span>
      <ul>
        <li>Customized ad strategies tailored to your goals</li>
        <li>Landing pages and creatives production</li>
        <li>Continuous campaign monitoring and optimization</li>
      </ul>
    </div>
  );

  // ── Wire Invoice Instructions View ─────────────────────────────────
  if (wireInvoice) {
    return (
      <DrawerCustom
        title="Wire transfer invoice"
        open={isOpen}
        onClose={handleCloseDrawer}
        placement="right"
        extra={<CloseOutlined onClick={handleCloseDrawer} />}
        closeIcon={false}
        autoFocus={false}
        className="drawer-body"
        width={md ? 450 : '100%'}
        mask
        footer={
          <footer className="drawer-footer">
            <Button size="large" block onClick={handleCopyBankDetails} icon={<CopyOutlined />}>
              Copy bank details
            </Button>
            <Button
              size="large"
              block
              type="primary"
              onClick={handleDownloadInvoice}
              icon={<DownloadOutlined />}
            >
              Download invoice
            </Button>
          </footer>
        }
        rootStyle={md ? { top: 'var(--shell-top, 62px)' } : undefined}
      >
        <div className="drawer-content" data-test-id="wire-invoice-drawer">
          <WireInvoiceInstructions
            wireInvoice={wireInvoice}
            onCopyToClipboard={handleCopyToClipboard}
            translateMap={subscriptionTranslateMap}
            currencyCode={currencyCode}
          />
        </div>
      </DrawerCustom>
    );
  }

  // ── Main Form View ─────────────────────────────────────────────────
  return (
    <DrawerCustom
      title="Top up balance"
      open={isOpen}
      onClose={handleCloseDrawer}
      placement="right"
      extra={<CloseOutlined onClick={handleCloseDrawer} />}
      closeIcon={false}
      autoFocus={false}
      className="drawer-body"
      width={md ? 450 : '100%'}
      mask
      footer={
        <footer className="drawer-footer">
          <Button size="large" block type="text" onClick={handleCloseDrawer}>
            Cancel
          </Button>
          <Button
            size="large"
            block
            type="primary"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={onSubmit}
            loading={topUpLoading || isCardOrderPending}
          >
            {paymentMethod === 'card'
              ? amount
                ? `Pay ${formatAmount(cardPayTotal)}`
                : 'Top up'
              : 'Create invoice'}
          </Button>
        </footer>
      }
      rootStyle={md ? { top: 'var(--shell-top, 62px)' } : undefined}
    >
      <div className="drawer-content w-full" data-test-id="top-up-drawer">
        {/* ── Payment Amount ──────────────────────────────────────── */}
        <span className="topup-label">Payment amount</span>

        <InputNumber<number>
          className="topup-amount-input"
          value={amount}
          status={topUpForm.errors.amount ? 'error' : ''}
          prefix={currencySymbol}
          formatter={(value) => `${value || ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value?.replace(/[^\d.]/g, '').replace(/,/g, '') as unknown as number}
          onChange={onChange}
          onBlur={() => topUpForm.validateField('amount')}
          placeholder=""
        />
        {topUpForm.errors.amount ? (
          <Typography.Text type="danger">{topUpForm.errors.amount}</Typography.Text>
        ) : null}

        {/* ── Predefined Amounts: the design's tile select ────────── */}
        {amountOptions.length > 0 && (
          <Radio.Group
            className="predefined-amounts"
            value={amount !== null && amountOptions.includes(amount) ? amount : null}
            onChange={(e) => onChange(e.target.value as number)}
          >
            {amountOptions.map((optionAmount) => {
              const ariaLabel = formatCurrencyAmount(optionAmount, currencyCode ?? 'USD', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
              const tier = getTierByAmount(optionAmount);
              // the tile sits in a block anchor: antd Radio hands its ref (and extra props) to
              // the hidden input, so a tooltip or a test id on the tile itself lands on a 0×0 box
              const tile = (
                <span
                  key={optionAmount}
                  className="topup-tile__anchor"
                  data-test-id={`top-up-amount-option-${optionAmount}`}
                >
                  <Radio.Button value={optionAmount} className="topup-tile" aria-label={ariaLabel}>
                    <span className="topup-tile__value">{formatAmount(optionAmount)}</span>
                    {tier ? (
                      <span className="topup-tile__sub">
                        <GiftIcon />+{tier.months} {tier.months === 1 ? 'month' : 'months'} PRO
                      </span>
                    ) : null}
                  </Radio.Button>
                </span>
              );
              if (!tier) return tile;

              return (
                <Tooltip
                  key={optionAmount}
                  title={getTooltipTitle(tier)}
                  placement="bottom"
                  arrow={false}
                  mouseEnterDelay={0.2}
                  mouseLeaveDelay={0}
                  destroyTooltipOnHide
                  overlayStyle={{ maxWidth: 400 }}
                >
                  {tile}
                </Tooltip>
              );
            })}
          </Radio.Group>
        )}

        {/* ── Payment Method Selector ─────────────────────────────── */}
        <div className="mt-24">
          <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} />
        </div>

        {/* ── Subscription Add-on: a wire invoice may carry PRO; a card
            order carries it locked on while the organization has none ── */}
        {subscriptionPricePerMonth > 0 &&
          (paymentMethod === 'wire_transfer' || (paymentMethod === 'card' && needsPro)) && (
            <div className="subscription-addon mt-24">
              <div className="subscription-addon__header">
                <div className="subscription-addon__info">
                  <TextBodySemiBold className="text-neutral-10">
                    Add a Boost PRO subscription
                  </TextBodySemiBold>
                  <span className="subscription-addon__sub">
                    {formatCurrencyAmount(subscriptionPricePerMonth, currencyCode ?? 'USD', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    /month · {paymentMethod === 'card' ? 'Renews monthly' : 'No auto-renewal'}
                  </span>
                </div>
                <Switch
                  checked={includeSubscription}
                  onChange={setIncludeSubscription}
                  // nothing launches without PRO — a card order cannot drop it
                  disabled={paymentMethod === 'card'}
                  data-test-id="include-subscription-toggle"
                />
              </div>

              {includeSubscription && paymentMethod === 'wire_transfer' && (
                <div className="subscription-addon__body mt-16">
                  <SubscriptionMonthsSelector
                    months={subscriptionMonths}
                    onChange={setSubscriptionMonths}
                  />
                </div>
              )}
            </div>
          )}

        {/* ── Order Summary (from server preview) ─────────────────── */}
        {showOrderSummary && (
          <div className="mt-24">
            <OrderSummary
              items={previewRows}
              total={preview?.data?.total ?? 0}
              currencyCode={currencyCode}
              isLoading={previewLoading}
            />
          </div>
        )}

        {paymentMethod === 'card' && !includeSubscription && (
          <p className="topup-note">Nothing is spent until a campaign launches.</p>
        )}
      </div>
    </DrawerCustom>
  );
};

export default TopUpDrawer;
